import { useState, useEffect } from 'react';
import { Activity, Database, BarChart3, TrendingUp, Key, Shield, Book, Download, FileText, Users, ChevronLeft, ChevronRight, CloudDownload, ArrowDown01, ChartNetwork, CreditCard } from 'lucide-react';
import PageLayout from '@/components/layout/PageLayout';
import StatsCard from '@/components/dashboard/StatsCard';
import ActivityLogTable, { ActivityLogItem } from '@/components/dashboard/ActivityLogTable';
import UsageQuotaCard from '@/components/dashboard/UsageQuotaCard';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Link } from 'react-router-dom';
import { auditService, AuditLogDto } from '@/services/auditService';
import { statisticService, UsageMetric } from '@/services/statisticService';
import { Progress } from '@/components/ui/progress';
import { useAuth } from '@/contexts/AuthContext';
import { handleSubscriptionUpgrade } from '@/lib/subscriptionUtils';
import { formatLocalDateTime } from '@/lib/date';

// Function to convert audit log to activity log format
const convertAuditLogToActivity = (auditLog: AuditLogDto): ActivityLogItem => {
  const actionMap: Record<string, string> = {
    'MemoryWrite': 'Memory write',
    'MemoryRecall': 'Memory recall',
    'MemoryExport': 'Data export',
    'MemoryDelete': 'Right-to-be-forgotten',
    'ConsentDenied': 'Consent denied'
  };

  return {
    id: auditLog.id,
    timestamp: formatLocalDateTime(auditLog.timestamp),
    action: actionMap[auditLog.eventType] || auditLog.eventType,
    project: auditLog.projectId || 'System',
    scope: auditLog.userId || 'system'
  };
};

// Function to get icon for usage metric
const getUsageMetricIcon = (metricName: string) => {
  switch (metricName.toLowerCase()) {
    case 'credits':
      return <CreditCard size={18} className="text-green-500" />;
    case 'memory reranking calls':
      return <ArrowDown01 size={18} className="text-blue-500" />;
    case 'total graph memory nodes':
    case 'graph memories':
      return <ChartNetwork size={18} className="text-recallio-green" />;
    case 'total memories':
    case 'memories':
      return <Database size={18} className="text-recallio-yellow" />;
    case 'memory retrieval calls':
    case 'memory sources':
      return <CloudDownload size={18} className="text-purple-500" />;
    default:
      return <Activity size={18} className="text-gray-500" />;
  }
};

const Dashboard = () => {
  const { isAuthenticated } = useAuth();
  const [recentActivities, setRecentActivities] = useState<ActivityLogItem[]>([]);
  const [loadingActivities, setLoadingActivities] = useState(true);
  const [usageMetrics, setUsageMetrics] = useState<UsageMetric[]>([]);
  const [loadingMetrics, setLoadingMetrics] = useState(true);
  const [statsData, setStatsData] = useState({
    memoryWrites7d: 0,
    memoryRecalls7d: 0,
    activeEntries: 0,
    complianceActions: 0
  });
  const [loadingStats, setLoadingStats] = useState(true);

  // Handle subscription parameter on dashboard load
  useEffect(() => {
    const handleSubscriptionParameter = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const subscription = urlParams.get('subscription');
      
      // Check both URL parameter and localStorage for subscription
      const urlSubscription = subscription && ['1', '2', '3', '4', '5'].includes(subscription) ? subscription : null;
      const localStorageSubscription = localStorage.getItem('selected-subscription');
      
      console.log('Dashboard subscription check:', {
        urlSubscription,
        localStorageSubscription,
        isAuthenticated,
        currentLocation: window.location.href
      });
      
      const finalSubscription = urlSubscription || localStorageSubscription;
      
      if (finalSubscription && ['1', '2', '3', '4', '5'].includes(finalSubscription)) {
        console.log('Processing subscription:', finalSubscription);
        
        if (isAuthenticated) {
          // User is logged in, redirect to billing page with subscription parameter
          console.log('User is authenticated, redirecting to billing page');
          localStorage.setItem('selected-subscription', finalSubscription);
          window.location.href = `/billing?subscription=${finalSubscription}`;
        }
      } else {
        console.log('No subscription found or invalid subscription');
      }
    };

    handleSubscriptionParameter();
  }, [isAuthenticated]);

  // Fetch recent audit logs for dashboard
  useEffect(() => {
    const fetchRecentActivities = async () => {
      try {
        setLoadingActivities(true);
        const response = await auditService.getAuditLogs({
          page: 1,
          pageSize: 5 // Get last 5 entries
        });
        
        const activities = response.logs.map(convertAuditLogToActivity);
        setRecentActivities(activities);
      } catch (error) {
        console.error('Error fetching recent activities:', error);
        // Fallback to empty array if API fails
        setRecentActivities([]);
      } finally {
        setLoadingActivities(false);
      }
    };

    fetchRecentActivities();
  }, []);

  // Fetch usage statistics for dashboard
  useEffect(() => {
    const fetchUsageMetrics = async () => {
      try {
        setLoadingMetrics(true);
        const response = await statisticService.getUsageStats({
          timeRange: 'current' // Get current month data
        });
        
        setUsageMetrics(response.usageMetrics);
      } catch (error) {
        console.error('Error fetching usage metrics:', error);
        // Fallback to empty array if API fails
        setUsageMetrics([]);
      } finally {
        setLoadingMetrics(false);
      }
    };

    fetchUsageMetrics();
  }, []);

  // Fetch stats data for dashboard cards
  useEffect(() => {
    const fetchStatsData = async () => {
      try {
        setLoadingStats(true);
        
        // Fetch data from multiple sources
        const [usageHistory, memoryStats, apiCallStats] = await Promise.all([
          statisticService.getUsageHistory(
            new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(), // 7 days ago
            new Date().toISOString() // now
          ),
          statisticService.getMemoryStatistics(),
          statisticService.getApiCallStatistics(
            new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(), // 7 days ago
            new Date().toISOString() // now
          )
        ]);

        // Process usage history to get writes and recalls
        const memoryWrites = usageHistory
          .filter(item => item.eventType.toLowerCase().includes('write') || item.eventType === 'MemoryWrite')
          .reduce((sum, item) => sum + item.count, 0);
          
        const memoryRecalls = usageHistory
          .filter(item => item.eventType.toLowerCase().includes('recall') || item.eventType === 'MemoryRecall')
          .reduce((sum, item) => sum + item.count, 0);

        // Get compliance actions (exports, deletes, etc.)
        const complianceActions = usageHistory
          .filter(item => 
            item.eventType.toLowerCase().includes('export') || 
            item.eventType.toLowerCase().includes('delete') ||
            item.eventType === 'MemoryExport' ||
            item.eventType === 'MemoryDelete'
          )
          .reduce((sum, item) => sum + item.count, 0);

        setStatsData({
          memoryWrites7d: memoryWrites,
          memoryRecalls7d: memoryRecalls,
          activeEntries: memoryStats.memoryObjects || memoryStats.totalMemories,
          complianceActions: complianceActions
        });
      } catch (error) {
        console.error('Error fetching stats data:', error);
        // Keep default values on error
      } finally {
        setLoadingStats(false);
      }
    };

    fetchStatsData();
  }, []);

  return (
    <div className="flex min-h-screen">
      {/* Main Dashboard Content */}
      <div className="flex-1">
        <PageLayout title="Dashboard">
          <div className="flex flex-wrap items-center justify-between mb-8 gap-4 w-full">
            <div className="flex-grow max-w-full">
              <p className="text-muted-foreground truncate">
                Monitor your compliance-first memory infrastructure and start integrating with our API.
              </p>
            </div>
            <div className="flex gap-2">
              <Button asChild className="btn-primary shrink-0">
                <Link to="/api-docs" className="flex items-center gap-2">
                  <Book size={16} />
                  API Documentation
                </Link>
              </Button>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8 w-full">
            {loadingMetrics ? (
              <div className="col-span-4 flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                <span className="ml-2 text-muted-foreground">Loading statistics...</span>
              </div>
            ) : (
              usageMetrics.map((metric, index) => (
                <Card key={index} className="overflow-hidden">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {getUsageMetricIcon(metric.name)}
                        <CardTitle className="text-sm font-medium">{metric.name}</CardTitle>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex justify-between items-baseline">
                        <span className="text-2xl font-bold">{metric.used.toLocaleString()}</span>
                        <span className="text-sm text-muted-foreground">
                          of {metric.limit === -1 ? "∞" : metric.limit.toLocaleString()} {metric.unit}
                        </span>
                      </div>
                      <Progress value={metric.percentage} className="h-2" />
                      <div className="flex justify-between text-xs text-muted-foreground">
                        <span>{metric.limit === -1 ? "" : (metric.percentage.toFixed(1) + "% used")}</span>
                        <span>{metric.limit === -1 ? "" : ("Per " + metric.term)}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>

          {/* Quick Actions */}
          <div className="mb-8 w-full">
            <h2 className="text-xl font-bold mb-4">Quick Actions</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <Card className="cursor-pointer hover:shadow-lg transition-shadow">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Key className="h-5 w-5 text-primary" />
                    Create API Key
                  </CardTitle>
                  <CardDescription className="text-sm">
                    Generate a new API key for your project
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button asChild size="sm" className="w-full">
                    <Link to="/api-keys">Create Key</Link>
                  </Button>
                </CardContent>
              </Card>

              <Card className="cursor-pointer hover:shadow-lg transition-shadow">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Book className="h-5 w-5 text-primary" />
                    API Documentation
                  </CardTitle>
                  <CardDescription className="text-sm">
                    Complete API reference and examples
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button asChild size="sm" className="w-full">
                    <Link to="/api-docs">View Docs</Link>
                  </Button>
                </CardContent>
              </Card>

              <Card className="cursor-pointer hover:shadow-lg transition-shadow">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Shield className="h-5 w-5 text-primary" />
                    Audit Logs
                  </CardTitle>
                  <CardDescription className="text-sm">
                    View compliance and security logs
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button asChild size="sm" className="w-full">
                    <Link to="/audit-logs">View Logs</Link>
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Recent Activity */}
          <div className="w-full">
            <h2 className="text-xl font-bold mb-4">Recent API Activity</h2>
            <div className="w-full overflow-x-auto">
              {loadingActivities ? (
                <div className="flex items-center justify-center h-32">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                  <span className="ml-2 text-muted-foreground">Loading recent activities...</span>
                </div>
              ) : (
                <ActivityLogTable activities={recentActivities} />
              )}
            </div>
          </div>
        </PageLayout>
      </div>
    </div>
  );
};

export default Dashboard;
